import { useTranslation } from "react-i18next";
import React, { useMemo } from "react";
import { SimpleSearch } from "../../../../../reduxToolkit/Search/SearchApi";
import { GetFoodieShiftResourceNotAssigned } from "../../../../../reduxToolkit/FoodieShift/FoodieShiftApi";
import { SEARCH_MODEL } from "../../../../../components/table/Model/TableModel";
import { TABLES } from "../../../../../Apis/Tables";
import { USER_TYPE_MODEL } from "../../../../../enums/userTypeEnum";
import { FOODIE_SHIFT_RESOURCE, FOODIE_SHIFT_RESOURCE_MODE } from "../Model/Models";
import DepartmentTotalEmployeesCard
    from "../../../../../components/CustomAvailableAssignedItems/SubComponents/DepartmentTotalEmployeesCard";
import SelectedDepartmentTotalEmployeesCard
    from "../../../../../components/CustomAvailableAssignedItems/SubComponents/SelectedDepartmentTotalEmployeesCard";
import { filterByNameEmailPhoneNumber, getFullName } from "../../../../../Apis/functions";
import CafeteriaCard from "../SubComponents/CafeteriaCard";
import SelectedCafeteriaCard from "../SubComponents/SelectedCafeteriaCard";
import {
    IconChipDepartment,
    IconChipMultiplePerson,
    IconKitchenTickets
} from "../../../../../components/buttons/IconButtons";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 *
 * Resolves the search configuration for the Add tab of a foodie shift section.
 * Mirrors the attendance AddShiftResourceApp dynamicConfig:
 *  - create (no id): simple search over the full catalog (client-side selection).
 *  - update (id present): not-assigned endpoint, so the catalog excludes items
 *    already assigned to the shift.
 *
 * @param {Object} props
 * @param {string} props.resource - One of FOODIE_SHIFT_RESOURCE.
 * @param {number|string|null|undefined} props.id - Shift id; absent in create.
 * @returns {{t: Function, dynamicConfig: Object}}
 */
export const AddShiftResourceApp = ({ resource, id }) => {
    const { t } = useTranslation();

    const isCreate = id === null || id === undefined;

    /**
     * Renders an available department card (name + employee count) for the catalog.
     * @param {Object} args - Available-item render args from the table.
     * @returns {JSX.Element}
     */
    const renderDepartmentsItem = ({ item, keyId, disabled, onAdd }) => {
        return (
            <DepartmentTotalEmployeesCard
                item={{
                    [keyId]: item[keyId],
                    fullName: item?.fullName || getFullName(
                        item?.name,
                        item?.firstLastName,
                        item?.secondLastName
                    ),
                    totalEmployees: item?.totalEmployees ?? item?.employeeCount,
                    disabled
                }}
                onAdd={onAdd}
            />
        )
    };

    /**
     * Renders the selected departments column, filtered by the section search.
     * @param {Object} args - Selected-items render args from the table.
     * @returns {JSX.Element[]}
     */
    const renderDepartmentsSelectedItems = ({ items, keyId, selectedSearchElement, handleDelete }) => {
        return (
            filterByNameEmailPhoneNumber(items, selectedSearchElement)?.map((item, index) => (
                <div className={"mb-3"} key={item[keyId] || index}>
                    <SelectedDepartmentTotalEmployeesCard
                        keyId={keyId}
                        item={{ ...item, totalEmployees: item?.totalEmployees ?? item?.employeeCount, disabled: false }}
                        onDelete={handleDelete}
                    />
                </div>
            ))
        )
    };

    /**
     * Renders an available cafeteria (diner) card for the catalog.
     * @param {Object} args - Available-item render args from the table.
     * @returns {JSX.Element}
     */
    const renderCafeteriaItems = ({ item, keyId, disabled, onAdd }) => {
        return (
            <CafeteriaCard
                item={{
                    [keyId]: item[keyId],
                    fullName: item?.fullName || getFullName(
                        item?.name,
                        item?.firstLastName,
                        item?.secondLastName
                    ),
                    disabled
                }}
                onAdd={onAdd}
            />
        )
    };

    /**
     * Renders the selected cafeterias column, filtered by the section search.
     * @param {Object} args - Selected-items render args from the table.
     * @returns {JSX.Element[]}
     */
    const renderCafeteriaSelectedItems = ({ items, keyId, selectedSearchElement, handleDelete }) => {
        return (
            filterByNameEmailPhoneNumber(items, selectedSearchElement)?.map((item, index) => (
                <div className={"mb-3"} key={item[keyId] || index}>
                    <SelectedCafeteriaCard
                        keyId={keyId}
                        item={{ ...item, disabled: false }}
                        onDelete={handleDelete}
                    />
                </div>
            ))
        )
    };

    const dynamicConfig = useMemo(() => {
        const mode = FOODIE_SHIFT_RESOURCE_MODE[resource];
        /**
         * Not-assigned request body (update mode). `model` is required non-empty
         * by validation but ignored by the backend logic; the mode is carried by
         * resourceString.
         */
        const notAssignedBody = ({ page, rowsPerPage, searchElement, model }) => {
            const body = SEARCH_MODEL.setObject({
                page,
                size: rowsPerPage,
                key: searchElement,
                model
            });
            body.resourceId = id;
            body.resourceString = mode;
            return body;
        }

        if (resource === FOODIE_SHIFT_RESOURCE.CAFETERIAS) {
            return {
                api: isCreate ? SimpleSearch : GetFoodieShiftResourceNotAssigned,
                keyId: 'id',
                labelRowsPerPage: 'diners_per_page',
                tableNotInformationLegend: 'no_results_found',
                titleContainer: 'available_diners',
                selectTitleContainer: 'selected_diners',
                searchLabel: 'search_by_name',
                selectedSectionIconTitle :<IconKitchenTickets height={18} width={18} className={"mr-8px mb-1 icon-color-general-950"} />,
                buildBody: ({ page, rowsPerPage, searchElement }) => isCreate
                    ? {
                        body: SEARCH_MODEL.setObject({
                            page,
                            size: rowsPerPage,
                            key: searchElement,
                            model: TABLES.FOODIE_CAFETERIA
                        })
                    }
                    : notAssignedBody({ page, rowsPerPage, searchElement, model: TABLES.FOODIE_CAFETERIA }),
                renderAvailableItem: renderCafeteriaItems,
                renderSelectedItems: renderCafeteriaSelectedItems
            };
        }

        if (resource === FOODIE_SHIFT_RESOURCE.DEPARTMENTS) {
            return {
                api: isCreate ? SimpleSearch : GetFoodieShiftResourceNotAssigned,
                keyId: 'id',
                labelRowsPerPage: 'departments_per_page',
                tableNotInformationLegend: 'no_results_found',
                titleContainer: 'available_departments',
                selectTitleContainer: 'selected_departments',
                searchLabel: 'search_by_name',
                selectedSectionIconTitle :<IconChipDepartment height={18} width={18} className={"mr-8px mb-1 icon-color-general-950"} />,
                buildBody: ({ page, rowsPerPage, searchElement }) => isCreate
                    ? {
                        body: SEARCH_MODEL.setObject({
                            page,
                            size: rowsPerPage,
                            key: searchElement,
                            model: TABLES.DEPARTMENT_SHIFT
                        })
                    }
                    : notAssignedBody({ page, rowsPerPage, searchElement, model: TABLES.DEPARTMENT }),
                renderAvailableItem: renderDepartmentsItem,
                renderSelectedItems: renderDepartmentsSelectedItems
            };
        }

        return {
            api: isCreate ? SimpleSearch : GetFoodieShiftResourceNotAssigned,
            keyId: 'userId',
            labelRowsPerPage: 'employees_per_page',
            tableNotInformationLegend: 'no_results_found',
            titleContainer: 'available_employees',
            selectTitleContainer: 'selected_employees',
            searchLabel: 'search_by_name_email_or_phone',
            selectedSectionIconTitle :<IconChipMultiplePerson height={18} width={18} className={"mr-8px mb-1 icon-color-general-950"} />,
            buildBody: ({ page, rowsPerPage, searchElement }) => isCreate
                ? {
                    body: SEARCH_MODEL.setObject({
                        page,
                        size: rowsPerPage,
                        key: searchElement,
                        model: TABLES.USER_BY_USER_TYPE,
                        userTypeIds: [
                            USER_TYPE_MODEL.VISITOR,
                            USER_TYPE_MODEL.EMPLOYEE,
                            USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE,
                            USER_TYPE_MODEL.SUPPLIER_IN_CHARGE
                        ]
                    })
                }
                : notAssignedBody({ page, rowsPerPage, searchElement, model: TABLES.USER_BY_USER_TYPE })
        };
    }, [resource, isCreate, id]);

    return { t, dynamicConfig };
}
